/* global __app_id, __firebase_config, __initial_auth_token */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import {
    getAuth,
    onAuthStateChanged,
    connectAuthEmulator,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    GoogleAuthProvider,
    signInWithPopup,
    signOut,
    signInWithCustomToken,
    signInAnonymously
} from 'firebase/auth';
import {
    getFirestore,
    doc,
    setDoc,
    updateDoc,
    serverTimestamp,
    connectFirestoreEmulator,
    collection,
    addDoc,
    query,
    orderBy,
    limit,
    getDocs,
    onSnapshot
} from 'firebase/firestore';
import { ChevronDown, ChevronUp, Phone, MessageSquare, AlertTriangle, CheckCircle, Star, TrendingUp, UploadCloud, XCircle, Info, LogOut, Award, Mail } from 'lucide-react';

// --- Firebase Configuration Section ---
// This configuration is now hardcoded as per your request to ensure
// the app always connects to your specific Firebase project.
const firebaseConfig = {
  apiKey: "AIzaSyBWanw9YpKGrzqcNCTEMf-s1TU4S3p7aMI",
  authDomain: "performanceclubtracker.firebaseapp.com",
  projectId: "performanceclubtracker",
  storageBucket: "performanceclubtracker.appspot.com",
  messagingSenderId: "34954709946",
  appId: "1:34954709946:web:e6bae51702e27bcd226b83",
  measurementId: "G-CZ2389BN31"
};

let firestoreDataAppId = firebaseConfig.projectId;


// --- Constants (PC Tracker Specific) ---
const PC_QUALIFICATION_POINTS = 150;
const MAX_CARRYOVER_POINTS = 150;
const BONUS_LEVELS = [
    { name: 'PC', minMonths: 3, maxMonths: 11, bonus: 200, nextLevelMonths: 12 },
    { name: 'Legend', minMonths: 12, maxMonths: 23, bonus: 300, nextLevelMonths: 24 },
    { name: 'Century', minMonths: 24, maxMonths: 35, bonus: 400, nextLevelMonths: 36 },
    { name: 'PC Shield', minMonths: 36, maxMonths: Infinity, bonus: 500, nextLevelMonths: null },
];

// --- Helper Functions (PC Tracker Specific) ---
const parseCSV = (csvText) => {
    if (!csvText || typeof csvText !== 'string') {
        console.warn("parseCSV received invalid input:", csvText);
        throw new Error("Invalid CSV data provided.");
    }
    const lines = csvText.split('\n');
    let reportMonthName = null, reportYear = null;
    const dateRangeLineIndex = lines.findIndex(line => line.toLowerCase().includes("date range:"));
    if (dateRangeLineIndex !== -1) {
        const match = lines[dateRangeLineIndex].match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (match) {
            reportYear = parseInt(match[3], 10);
            reportMonthName = new Date(reportYear, parseInt(match[1], 10) - 1, 1).toLocaleString('default', { month: 'long' });
        }
    }
    let headerRowIndex = lines.findIndex(line => line.includes("Associate Name") && line.includes("Total Premium"));
    if (headerRowIndex === -1) headerRowIndex = lines.findIndex(line => line.split(',').length > 5 && line.toLowerCase().includes("name"));
    if (headerRowIndex === -1) throw new Error("CSV header row not found. Ensure 'Associate Name' and 'Total Premium' columns are present or a similar header structure.");

    const headers = lines[headerRowIndex].split(',').map(h => h.trim().replace(/"/g, ''));
    const dataLines = lines.slice(headerRowIndex + 1);

    return {
        associates: dataLines.map(line => {
            const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
            if (values.length === 1 && values[0] === '') return null;
            let entry = {}; headers.forEach((header, i) => { entry[header] = values[i] || ""; });
            if (!entry['Associate Name'] && !entry['Total Premium']) return null;
            return entry;
        }).filter(Boolean),
        reportMonthName, reportYear
    };
};
const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();
const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};


// --- Main App Component ---
const App = () => {
    // Firebase instances state
    const [auth, setAuth] = useState(null);
    const [db, setDb] = useState(null);
    const [isFirebaseReady, setIsFirebaseReady] = useState(false);
    const [firebaseInitError, setFirebaseInitError] = useState('');

    // Auth State
    const [currentUser, setCurrentUser] = useState(null);
    const [userId, setUserId] = useState(null);
    const [authError, setAuthError] = useState('');
    const [authLoading, setAuthLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSignUp, setIsSignUp] = useState(false);
    const [isPremiumUser, setIsPremiumUser] = useState(false);
    const [userTier, setUserTier] = useState('free');

    // PC Tracker State
    const [csvDataText, setCsvDataText] = useState(null);
    const [processedAssociates, setProcessedAssociates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'totalPremium', direction: 'descending' });
    const [focusSortConfig, setFocusSortConfig] = useState({ key: 'pointsNeeded', direction: 'ascending' });
    const [currentReportMonthName, setCurrentReportMonthName] = useState('');
    const [daysLeftInReportMonth, setDaysLeftInReportMonth] = useState(0);
    const [isLastQuarterOfReportMonth, setIsLastQuarterOfReportMonth] = useState(false);
    const [showUploadPrompt, setShowUploadPrompt] = useState(true);
    const [fileName, setFileName] = useState('');
    const [lastUploadedDocInfo, setLastUploadedDocInfo] = useState(null);
    const [isProcessingLastDoc, setIsProcessingLastDoc] = useState(false);

    // Initialize Firebase
    useEffect(() => {
        if (!firebaseConfig || !firebaseConfig.projectId) {
            const errMsg = "Firebase configuration is missing or invalid. App cannot connect to Firebase.";
            setFirebaseInitError(errMsg);
            setAuthLoading(false);
            setIsFirebaseReady(false);
            return;
        }
        try {
            const firebaseApp = initializeApp(firebaseConfig);
            const authInstance = getAuth(firebaseApp);
            const dbInstance = getFirestore(firebaseApp);
            setAuth(authInstance);
            setDb(dbInstance);
        } catch (e) {
            console.error("[GLOBAL ERROR] Error initializing Firebase:", e);
            setFirebaseInitError("Failed to initialize Firebase services: " + e.message);
            setAuthLoading(false);
            setIsFirebaseReady(false);
            return;
        }
        setIsFirebaseReady(true);
    }, []);

    // Auth State Change Listener & User Data Fetching
    useEffect(() => {
        if (!isFirebaseReady || !auth || !db || !firestoreDataAppId) {
            if (isFirebaseReady && (!auth || !db)) {
                 setAuthLoading(false);
            }
            if (isFirebaseReady && !firestoreDataAppId) {
                setError("Application configuration error preventing data access.");
                setAuthLoading(false);
            }
            return;
        }
        
        // --- MODIFICATION START: Conditional Tier Logic ---
        // This now checks for your actual premium domain.
        const premiumHostname = 'pcp.wearetnv.com'; 
        const isPremiumSite = window.location.hostname === premiumHostname;
        // For local testing, you could also use:
        // const isPremiumSite = window.location.hostname === 'localhost' || window.location.hostname === premiumHostname;

        setAuthLoading(true);
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setCurrentUser(user);
                const currentUid = user.uid;
                setUserId(currentUid);
                const userProfileRef = doc(db, `artifacts/${firestoreDataAppId}/users/${currentUid}/userProfile/profileData`);

                let unsubProfile;

                if (isPremiumSite) {
                    // --- LOGIC FOR THE NEW PREMIUM-ONLY WEBSITE ---
                    // This logic ensures any user on this site becomes premium.
                    unsubProfile = onSnapshot(userProfileRef, (profileSnap) => {
                        if (profileSnap.exists()) {
                            const userProfile = profileSnap.data();
                            // If the user's profile exists but isn't premium, update it.
                            if (userProfile.tier !== 'premium') {
                                updateDoc(userProfileRef, { tier: 'premium' }).catch(err => {
                                    console.error(`[APP ERROR] Failed to upgrade user ${currentUid} to premium:`, err);
                                });
                            }
                        } else {
                            // If the profile doesn't exist, create it with premium status.
                            setDoc(userProfileRef, {
                                email: user.email || 'N/A',
                                createdAt: serverTimestamp(),
                                tier: 'premium', // Default to premium
                                userId: currentUid,
                            }).catch(profileCreationError => {
                                console.error(`[APP ERROR] Fail to create premium profile for ${currentUid}:`, profileCreationError);
                                setError("Could not create user profile.");
                            });
                        }
                        // Immediately grant premium access on the client for a seamless experience.
                        setUserTier('premium');
                        setIsPremiumUser(true);
                    }, (error) => {
                        console.error(`[APP ERROR] Error listening to profile for ${currentUid}:`, error);
                        setError("Could not load user profile information.");
                        setUserTier('free');
                        setIsPremiumUser(false);
                    });
                } else {
                    // --- LOGIC FOR THE ORIGINAL TIERED WEBSITE ---
                    // This is the original logic that respects free/premium tiers.
                    unsubProfile = onSnapshot(userProfileRef, (profileSnap) => {
                        if (profileSnap.exists()) {
                            const userProfile = profileSnap.data();
                            setUserTier(userProfile.tier || 'free');
                            setIsPremiumUser(userProfile.tier === 'premium');
                        } else {
                            // New user on the original site defaults to 'free'.
                            setDoc(userProfileRef, {
                                email: user.email || 'N/A',
                                createdAt: serverTimestamp(),
                                tier: 'free',
                                userId: currentUid,
                            }).then(() => {
                                setUserTier('free');
                                setIsPremiumUser(false);
                            }).catch(profileCreationError => {
                                console.error(`[APP ERROR] Fail to create profile for ${currentUid}:`, profileCreationError);
                                setError("Could not create user profile.");
                            });
                        }
                    }, (error) => {
                        console.error(`[APP ERROR] Error listening to profile for ${currentUid}:`, error);
                        setError("Could not load user profile information.");
                        setUserTier('free');
                        setIsPremiumUser(false);
                    });
                }
                // --- MODIFICATION END ---


                setIsProcessingLastDoc(true);
                const docsCollectionRef = collection(db, `artifacts/${firestoreDataAppId}/users/${currentUid}/uploadedCsvs`);
                const q = query(docsCollectionRef, orderBy("originalUploadDate", "desc"), limit(1));
                try {
                    const querySnapshot = await getDocs(q);
                    if (!querySnapshot.empty) {
                        const lastDocData = querySnapshot.docs[0].data();
                        const lastDocId = querySnapshot.docs[0].id;
                        setLastUploadedDocInfo({ ...lastDocData, id: lastDocId });
                        setFileName(lastDocData.fileName);
                        setCsvDataText(lastDocData.csvText);
                        setShowUploadPrompt(false);
                    } else {
                        setLastUploadedDocInfo(null); setCsvDataText(null); setProcessedAssociates([]); setShowUploadPrompt(true);
                    }
                } catch (fetchError) {
                    console.error(`[APP ERROR] Error fetching last doc for ${currentUid}:`, fetchError);
                    setError("Could not load last uploaded document.");
                    setLastUploadedDocInfo(null); setShowUploadPrompt(true);
                } finally {
                     setIsProcessingLastDoc(false);
                }
                setAuthLoading(false);
                return () => { if (unsubProfile) unsubProfile(); };
            } else {
                setCurrentUser(null); setUserId(null); setIsPremiumUser(false); setUserTier('free');
                setLastUploadedDocInfo(null); setCsvDataText(null); setProcessedAssociates([]);
                setShowUploadPrompt(true); setFileName(''); setAuthLoading(false);
            }
        });
        if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token && auth.currentUser === null) {
            signInWithCustomToken(auth, __initial_auth_token)
                .catch((error) => {
                    console.error("[APP ERROR] Custom token sign-in error:", error);
                    if (!auth.currentUser) {
                        signInAnonymously(auth).catch(anonError => console.error("[APP ERROR] Anonymous sign-in error:", anonError));
                    }
                });
        }
        return () => unsubscribe();
    }, [isFirebaseReady, auth, db]);

    // --- processData DEFINITION ---
    const processData = useCallback((parsedData) => {
        if (!parsedData || !parsedData.associates) {
            console.warn("[PROCESS DATA] Invalid parsedData received:", parsedData);
            setError("Could not process the provided data.");
            setProcessedAssociates([]);
            return;
        }
        const { associates, reportMonthName: csvReportMonthName, reportYear: csvReportYear } = parsedData;
        const today = new Date();
        const currentSystemMonth = today.getMonth() + 1;
        const currentSystemYear = today.getFullYear();
        const currentSystemDay = today.getDate();
        let effectiveReportMonth, effectiveReportYear, reportMonthForDisplay;
        let daysLeft = 0, isLastQuarter = false; 
        if (csvReportMonthName && csvReportYear) {
            effectiveReportMonth = new Date(Date.parse(csvReportMonthName + " 1, " + csvReportYear)).getMonth() + 1;
            effectiveReportYear = csvReportYear;
            reportMonthForDisplay = csvReportMonthName;
        } else {
            effectiveReportMonth = currentSystemMonth; effectiveReportYear = currentSystemYear;
            reportMonthForDisplay = today.toLocaleString('default', { month: 'long' });
        }
        setCurrentReportMonthName(reportMonthForDisplay + " " + effectiveReportYear);
        const daysInEffectiveMonth = getDaysInMonth(effectiveReportYear, effectiveReportMonth);

        // Determine isLastQuarter based on the report's effective date relative to current system date
        if (effectiveReportYear < currentSystemYear || (effectiveReportYear === currentSystemYear && effectiveReportMonth < currentSystemMonth)) {
            daysLeft = 0; isLastQuarter = true; // For past months
        } else if (effectiveReportYear === currentSystemYear && effectiveReportMonth === currentSystemMonth) {
            daysLeft = Math.max(0, daysInEffectiveMonth - currentSystemDay); // For current month
            if (currentSystemDay > (daysInEffectiveMonth * 0.75)) {
                isLastQuarter = true;
            }
        } else { // Future month
            daysLeft = daysInEffectiveMonth; isLastQuarter = false;
        }
        setDaysLeftInReportMonth(daysLeft);
        setIsLastQuarterOfReportMonth(isLastQuarter); // This state is for the header message

        const processed = associates.map(assoc => {
            if (!assoc || !assoc['Associate Name']) return null;
            const totalPremium = parseFloat(assoc['Total Premium']) || 0;
            const consecutiveCount = parseInt(assoc['Current PC Consecutive Count']) || 0;
            const isQualified = totalPremium >= PC_QUALIFICATION_POINTS;
            const pointsNeeded = isQualified ? 0 : Math.max(0, PC_QUALIFICATION_POINTS - totalPremium);
            let carryoverPoints = 0;
            if (totalPremium > PC_QUALIFICATION_POINTS) carryoverPoints = Math.min(MAX_CARRYOVER_POINTS, totalPremium - PC_QUALIFICATION_POINTS);
            let bonus = { category: 'N/A', amount: 0, nextBonusIn: 'N/A', nextBonusName: '' };
            if (consecutiveCount > 0) {
                for (const level of BONUS_LEVELS) {
                    if (consecutiveCount >= level.minMonths && consecutiveCount <= level.maxMonths) {
                        bonus.category = level.name; bonus.amount = level.bonus;
                        if (level.nextLevelMonths) {
                            bonus.nextBonusIn = `${level.nextLevelMonths - consecutiveCount} month(s)`;
                            const nextL = BONUS_LEVELS.find(l => l.minMonths === level.nextLevelMonths);
                            bonus.nextBonusName = nextL ? `to ${nextL.name} ($${nextL.bonus})` : '';
                        } else bonus.nextBonusIn = 'Top Bonus Achieved';
                        break;
                    }
                }
                if (bonus.category === 'N/A' && consecutiveCount < BONUS_LEVELS[0].minMonths) {
                    bonus.nextBonusIn = `${BONUS_LEVELS[0].minMonths - consecutiveCount} month(s)`;
                    bonus.nextBonusName = `to ${BONUS_LEVELS[0].name} ($${BONUS_LEVELS[0].bonus})`;
                }
            } else {
                bonus.nextBonusIn = `${BONUS_LEVELS[0].minMonths} month(s)`;
                bonus.nextBonusName = `to ${BONUS_LEVELS[0].name} ($${BONUS_LEVELS[0].bonus})`;
            }
            let closeCategory = '', closeColor = '';
            if (!isQualified && pointsNeeded > 0) {
                if (pointsNeeded <= 50) { closeCategory = 'Close'; closeColor = 'bg-green-100 dark:bg-green-700'; }
                else if (pointsNeeded <= 100) { closeCategory = 'Midway'; closeColor = 'bg-yellow-100 dark:bg-yellow-700'; }
                else if (pointsNeeded < PC_QUALIFICATION_POINTS) { closeCategory = 'Near'; closeColor = 'bg-red-100 dark:bg-red-700'; }
            }
            
            // --- MODIFIED LOGIC FOR isInDanger ---
            // "Danger" if they have a streak and are not qualified for the report month being viewed.
            // The `isLastQuarter` variable (calculated above for the report month) is NO LONGER a factor here.
            const isInDangerCurrentProcessing = consecutiveCount > 0 && !isQualified;
            // --- END OF MODIFIED LOGIC ---

            return {
                ...assoc,
                id: assoc['Associate Ledger ID'] || assoc['Associate Name'] + Math.random().toString(16).slice(2),
                name: assoc['Associate Name'],
                phone: assoc['Phone Number'] || '',
                totalPremium, consecutiveCount, isQualified, pointsNeeded, carryoverPoints, bonus,
                closeCategory, closeColor,
                isInDanger: isInDangerCurrentProcessing
            };
        }).filter(Boolean);
        setProcessedAssociates(processed);
    }, [setCurrentReportMonthName, setDaysLeftInReportMonth, setIsLastQuarterOfReportMonth, setProcessedAssociates, setError]); // isLastQuarterOfReportMonth removed as it's SET here, not read before setting.

    // Process CSV data when csvDataText changes
    useEffect(() => {
        if (csvDataText && !isProcessingLastDoc) {
            setIsLoading(true); setError('');
            try {
                const parsed = parseCSV(csvDataText);
                processData(parsed);
            } catch (err) {
                console.error("[APP ERROR] Error parsing/processing CSV from text:", err);
                setError(`Error processing CSV: ${err.message}.`);
                setCsvDataText(null); setProcessedAssociates([]); setShowUploadPrompt(true);
            } finally {
                setIsLoading(false);
            }
        }
    }, [csvDataText, isProcessingLastDoc, processData, setIsLoading, setError, setCsvDataText, setProcessedAssociates, setShowUploadPrompt]);

    const handleEmailPasswordAuth = async (e) => {
        e.preventDefault();
        if (!auth) { setAuthError("Auth service not available."); return; }
        setAuthError(''); setAuthLoading(true);
        try {
            if (isSignUp) {
                await createUserWithEmailAndPassword(auth, email, password);
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
            setEmail(''); setPassword('');
        } catch (error) {
            console.error(`[APP ERROR] Email/Pass Auth Error (${isSignUp ? 'Sign Up' : 'Sign In'}):`, error);
            setAuthError(error.message);
            setAuthLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        if (!auth) { setAuthError("Auth service not available."); return; }
        setAuthError(''); setAuthLoading(true);
        const provider = new GoogleAuthProvider();
        try {
            await signInWithPopup(auth, provider);
        } catch (error) {
            console.error("[APP ERROR] Google Sign In Error:", error);
            setAuthError(error.message);
            setAuthLoading(false);
        }
    };

    const handleSignOut = async () => {
        if (!auth) { setAuthError("Auth service not available."); return; }
        setAuthLoading(true);
        try {
            await signOut(auth);
        } catch (error) {
            console.error("[APP ERROR] Sign Out Error:", error);
            setAuthError(error.message);
            setAuthLoading(false);
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (file && currentUser && db && firestoreDataAppId) {
            setIsLoading(true); setError(''); setFileName(file.name);
            const reader = new FileReader();
            reader.onload = async (e) => {
                const newCsvText = e.target.result;
                setCsvDataText(newCsvText);
                try {
                    const docRef = await addDoc(collection(db, `artifacts/${firestoreDataAppId}/users/${userId}/uploadedCsvs`), {
                        userId: userId,
                        fileName: file.name,
                        csvText: newCsvText,
                        originalUploadDate: serverTimestamp(),
                        processedDate: serverTimestamp()
                    });
                    setLastUploadedDocInfo({
                        id: docRef.id, fileName: file.name, csvText: newCsvText, originalUploadDate: new Date()
                    });
                    setShowUploadPrompt(false);
                } catch (dbError) {
                    console.error("[APP ERROR] Error saving CSV to Firestore:", dbError);
                    setError("Failed to save uploaded document. Please try again.");
                }
            };
            reader.onerror = () => { setError('Error reading file.'); setIsLoading(false); };
            reader.readAsText(file);
        } else if (!currentUser) { setError("You must be logged in to upload files."); }
        else if (!db) { setError("Database service is not available."); }
        else if (!firestoreDataAppId) { setError("Application configuration error for data storage."); }
    };

    const handleClearData = () => {
        setCsvDataText(null); setProcessedAssociates([]); setShowUploadPrompt(true);
        setFileName(''); setError(''); setLastUploadedDocInfo(null); setCurrentReportMonthName('');
    };

    const requestSort = (key, tableType = 'main') => {
        let direction = 'ascending';
        const currentSortConfig = tableType === 'main' ? sortConfig : focusSortConfig;
        const setSort = tableType === 'main' ? setSortConfig : setFocusSortConfig;
        if (currentSortConfig.key === key && currentSortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSort({ key, direction });
    };

    const sortedAssociates = useMemo(() => {
        let sortableItems = [...processedAssociates];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                const valA = sortConfig.key.startsWith('bonus.') ? a.bonus[sortConfig.key.split('.')[1]] : a[sortConfig.key];
                const valB = sortConfig.key.startsWith('bonus.') ? b.bonus[sortConfig.key.split('.')[1]] : b[sortConfig.key];
                if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [processedAssociates, sortConfig]);

    const focusZoneLists = useMemo(() => {
        const safeProcessedAssociates = Array.isArray(processedAssociates) ? processedAssociates : [];
        const qualified = safeProcessedAssociates.filter(a => a.isQualified);
        const closeToQualifying = safeProcessedAssociates.filter(a => !a.isQualified && a.pointsNeeded > 0 && a.pointsNeeded < PC_QUALIFICATION_POINTS);
        // `a.isInDanger` is now calculated in processData without the isLastQuarterOfReportMonth dependency for THIS list.
        const dangerZone = safeProcessedAssociates.filter(a => a.isInDanger);

        if (focusSortConfig.key) {
            [qualified, closeToQualifying, dangerZone].forEach(list => {
                list.sort((a, b) => {
                    let valA = focusSortConfig.key.startsWith('bonus.') ? a.bonus[focusSortConfig.key.split('.')[1]] : a[focusSortConfig.key];
                    let valB = focusSortConfig.key.startsWith('bonus.') ? b.bonus[focusSortConfig.key.split('.')[1]] : b[focusSortConfig.key];
                    if (valA < valB) return focusSortConfig.direction === 'ascending' ? -1 : 1;
                    if (valA > valB) return focusSortConfig.direction === 'ascending' ? 1 : -1;
                    return 0;
                });
            });
        }
        return { qualified, closeToQualifying, dangerZone };
    }, [processedAssociates, focusSortConfig]); // isLastQuarterOfReportMonth removed from here as isInDanger is pre-calculated

    const SortableTh = ({ children, sortKey, tableType = 'main' }) => {
        const currentSortConfig = tableType === 'main' ? sortConfig : focusSortConfig;
        const icon = currentSortConfig.key === sortKey ? (currentSortConfig.direction === 'ascending' ? <ChevronUp size={16} /> : <ChevronDown size={16} />) : null;
        return (<th className="p-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" onClick={() => requestSort(sortKey, tableType)}><div className="flex items-center space-x-1"><span>{children}</span>{icon}</div></th>);
    };

    const getSMSText = (associate, category) => {
        const monthNameToUse = currentReportMonthName || new Date().toLocaleString('default', { month: 'long' });
        if (category === 'qualified') return `Congratulations ${associate.name} for being PC qualified for ${monthNameToUse}! Points: ${associate.totalPremium.toFixed(0)}, Carryover: ${associate.carryoverPoints.toFixed(0)}.`;
        if (category === 'close') return `${associate.name}, you need ${associate.pointsNeeded.toFixed(0)} points for PC for ${monthNameToUse}. Let's help you qualify!`;
        if (category === 'danger') return `${associate.name}, you're in danger of losing PC streak for ${monthNameToUse}! Need ${associate.pointsNeeded.toFixed(0)} points. Let's connect!`;
        return `Hi ${associate.name}, checking PC progress for ${monthNameToUse}.`;
    };

    const AssociateCard = ({ associate, type }) => {
        return (<div className={`p-4 rounded-lg shadow-md ${type === 'qualified' ? 'bg-green-50 dark:bg-green-800 border-l-4 border-green-500' : associate.closeColor ? `${associate.closeColor} border-l-4 ${associate.closeCategory === 'Near' ? 'border-red-500' : associate.closeCategory === 'Midway' ? 'border-yellow-500' : 'border-green-500'}` : type === 'danger' ? 'bg-red-50 dark:bg-red-800 border-l-4 border-red-500' : 'bg-white dark:bg-gray-800'} mb-3`}>
            <div className="flex justify-between items-center"><h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{associate.name}</h4><div className="flex space-x-2">{associate.phone && (<><a href={`tel:${associate.phone}`} className="p-2 rounded-full hover:bg-blue-100 dark:hover:bg-blue-700 text-blue-600 dark:text-blue-300 transition-colors"><Phone size={18} /></a><a href={`sms:${associate.phone}?&body=${encodeURIComponent(getSMSText(associate, type))}`} className="p-2 rounded-full hover:bg-green-100 dark:hover:bg-green-700 text-green-600 dark:text-green-300 transition-colors"><MessageSquare size={18} /></a></>)}</div></div>
            <p className="text-sm text-gray-600 dark:text-gray-300">Points: <span className="font-medium">{associate.totalPremium.toFixed(0)}</span></p>
            {type === 'qualified' && <p className="text-sm text-gray-600 dark:text-gray-300">Carryover: <span className="font-medium">{associate.carryoverPoints.toFixed(0)}</span></p>}
            {type !== 'qualified' && <p className="text-sm text-gray-600 dark:text-gray-300">Needed: <span className="font-medium">{associate.pointsNeeded.toFixed(0)}</span></p>}
            <p className="text-sm text-gray-600 dark:text-gray-300">Months: <span className="font-medium">{associate.consecutiveCount}</span></p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Bonus: <span className="font-medium">{associate.bonus.category} (${associate.bonus.amount})</span></p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Next: <span className="font-medium">{associate.bonus.nextBonusIn} {associate.bonus.nextBonusName}</span></p>
        </div>);
    };

    const handleUpgradeToPremium = () => {
        if (!currentUser) {
            setError("Cannot request upgrade: User not logged in.");
            return;
        }
        const yourEmailAddress = 'herman@freedombychoice.com';
        const subject = encodeURIComponent("Premium Upgrade Request - Performance Club Tracker");
        const body = encodeURIComponent(
`Hello,

I am interested in upgrading to the Premium tier for the Performance Club Tracker.

My User Email: ${currentUser.email || 'Not available'}
My User ID: ${userId || 'Not available'}

Please provide me with more information on how to proceed.

Thanks,
${currentUser.displayName || currentUser.email || 'User'}`
        );
        window.location.href = `mailto:${yourEmailAddress}?subject=${subject}&body=${body}`;
        console.log(`[APP INFO] User ${userId} initiated premium upgrade request email to ${yourEmailAddress}.`);
    };

    // --- Conditional Rendering Logic ---
    // (The rest of the JSX for loading states, login form, and main app view remains the same)
    if (firebaseInitError) {
        return ( /* Error Display JSX */ 
            <div className="min-h-screen bg-red-100 dark:bg-red-900 flex items-center justify-center text-red-700 dark:text-red-200 p-4">
                <div className="text-center p-8 bg-white dark:bg-gray-800 shadow-lg rounded-lg">
                    <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold mb-2">Application Error</h1>
                    <p className="text-md">{firebaseInitError}</p>
                    <p className="text-sm mt-4">Please ensure your Firebase configuration is correct and try again.</p>
                </div>
            </div>
        );
    }

    if (authLoading || (!isFirebaseReady && !authError) || (isFirebaseReady && currentUser && isProcessingLastDoc)) {
        return ( /* Loading Spinner JSX */ 
            <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center text-gray-800 dark:text-gray-100">
                <div className="text-center p-8">
                    <svg className="animate-spin h-10 w-10 text-blue-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-xl font-semibold">
                        {authError ? "Authentication Error..." :
                         authLoading ? "Authenticating..." :
                         isProcessingLastDoc ? "Loading your data..." :
                         !isFirebaseReady ? "Initializing Firebase..." :
                         "Loading Application..."}
                    </p>
                    {authError && <p className="text-red-500 mt-2">{authError}</p>}
                </div>
            </div>
        );
    }

    if (!currentUser) {
        return ( /* Login Form JSX */ 
            <div className="min-h-screen bg-gradient-to-br from-slate-200 to-sky-200 dark:from-slate-800 dark:to-sky-900 flex flex-col items-center justify-center p-4">
                <div className="w-full max-w-md bg-white dark:bg-gray-800 p-8 rounded-xl shadow-2xl">
                    <div className="text-center mb-8">
                        <TrendingUp className="h-16 w-16 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                        <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-sky-500 dark:from-blue-400 dark:to-sky-300">
                            Performance Club Tracker
                        </h1>
                    </div>
                    <form onSubmit={handleEmailPasswordAuth} className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email address</label>
                            <input id="email" name="email" type="email" autoComplete="email" required
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
                        </div>
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
                            <input id="password" name="password" type="password" autoComplete="current-password" required
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                        </div>
                        {authError && <p className="text-sm text-red-600 dark:text-red-400 text-center">{authError}</p>}
                        <div className="flex flex-col space-y-3">
                            <button type="submit" disabled={authLoading || !isFirebaseReady}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50">
                                {authLoading ? 'Processing...' : (isSignUp ? 'Sign Up' : 'Sign In')}
                            </button>
                            <button type="button" onClick={handleGoogleSignIn} disabled={authLoading || !isFirebaseReady}
                                className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50">
                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true"> <path fillRule="evenodd" d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zM8.28 16.137A6.043 6.043 0 014.015 10c0-1.62.635-3.114 1.737-4.216l2.064 1.593A3.5 3.5 0 007.5 10c0 .802.273 1.53.733 2.101l-2.054 2.099a5.997 5.997 0 012.1-.063zm3.857-1.757c.48-.48.763-1.13.763-1.843a2.497 2.497 0 00-1.05-1.955L9.727 8.458A4.473 4.473 0 0110 5.5c1.056 0 2.018.365 2.773.975l2.05-2.05A8.013 8.013 0 0010 2C6.686 2 4 4.686 4 8s2.686 6 6 6c1.318 0 2.54-.428 3.537-1.16l-1.4-1.4z" clipRule="evenodd" /> </svg>
                                Sign In with Google
                            </button>
                        </div>
                    </form>
                    <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                        <button onClick={() => { setIsSignUp(!isSignUp); setAuthError(''); }} className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300">
                            {isSignUp ? 'Sign In' : 'Sign Up'}
                        </button>
                    </p>
                     <p className="mt-2 text-center text-xs text-gray-400 dark:text-gray-500">Data App ID: {firestoreDataAppId || "Not Set"}</p>
                </div>
            </div>
        );
    }

    // Main App View (User is logged in)
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-100 to-sky-100 dark:from-slate-800 dark:to-sky-900 p-4 md:p-8 font-sans text-gray-800 dark:text-gray-200">
            <div className="max-w-7xl mx-auto">
                <header className="mb-8 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
                    {/* Header JSX ... */}
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <div className="flex items-center space-x-3 mb-4 md:mb-0">
                            <TrendingUp className="h-10 w-10 text-blue-600 dark:text-blue-400" />
                            <div>
                                <h1 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-sky-500 dark:from-blue-400 dark:to-sky-300">Performance Club Tracker</h1>
                                {currentUser && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        <p>User: {currentUser.email || currentUser.displayName || currentUser.uid}</p>
                                        <p>Tier: <span className={`font-semibold ${isPremiumUser ? 'text-yellow-500' : 'text-gray-500'}`}>{userTier}</span>
                                            {isPremiumUser && <Star size={12} className="inline ml-1 text-yellow-500" />}
                                        </p>
                                        <p className="font-mono">UID: {userId}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <label htmlFor="csvUpload" className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-150 ease-in-out flex items-center space-x-2">
                                <UploadCloud size={20} />
                                <span>{fileName || 'Upload New CSV'}</span>
                            </label>
                            <input type="file" id="csvUpload" accept=".csv" onChange={handleFileUpload} className="hidden" />
                            {(csvDataText || (processedAssociates && processedAssociates.length > 0)) && (
                                <button onClick={handleClearData}
                                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-150 ease-in-out flex items-center space-x-2">
                                    <XCircle size={20} />
                                    <span>Clear Data</span>
                                </button>
                            )}
                            <button onClick={handleSignOut}
                                className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-150 ease-in-out flex items-center space-x-2">
                                <LogOut size={20} />
                                <span>Sign Out</span>
                            </button>
                        </div>
                    </div>
                    {currentReportMonthName && (
                        <div className="mt-4 text-center md:text-left border-t border-gray-200 dark:border-gray-700 pt-3">
                            <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                                Data for: <span className="text-blue-600 dark:text-blue-400">{currentReportMonthName}</span>
                                {lastUploadedDocInfo && lastUploadedDocInfo.originalUploadDate && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(Uploaded: {formatDate(lastUploadedDocInfo.originalUploadDate)})</span>
                                )}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Days left in {currentReportMonthName.split(" ")[0]}: <span className="font-bold">{daysLeftInReportMonth}</span>.
                                {isLastQuarterOfReportMonth && <span className="ml-2 text-red-500 font-semibold">(Last Quarter!)</span>}
                            </p>
                        </div>
                    )}
                </header>

                {isLoading && ( /* Loading CSV Spinner JSX */ 
                    <div className="text-center my-6"><div className="inline-block animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div><p className="text-lg font-semibold mt-3">Processing CSV data...</p></div>
                )}
                {error && ( /* Error Display JSX */
                    <div role="alert" className="my-4 p-4 bg-red-100 border border-red-400 text-red-700 dark:bg-red-800 dark:border-red-600 dark:text-red-200 rounded-lg shadow-md"><div className="flex items-center"><AlertTriangle className="h-5 w-5 text-red-500 mr-2" /> <p className="font-bold">Error!</p></div><p>{error}</p></div>
                )}

                {showUploadPrompt && !csvDataText && !isLoading && ( /* Upload Prompt JSX */
                     <div className="text-center p-10 bg-white dark:bg-gray-800 rounded-xl shadow-xl my-8">
                         <UploadCloud className="h-24 w-24 text-blue-500 dark:text-blue-400 mx-auto mb-6 opacity-70" />
                         <h2 className="text-2xl font-semibold mb-3 text-gray-700 dark:text-gray-200">Welcome, {currentUser.displayName || currentUser.email}!</h2>
                         {lastUploadedDocInfo ? (
                              <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md mx-auto">
                                  Last upload <span className="font-semibold">"{lastUploadedDocInfo.fileName}"</span> ({formatDate(lastUploadedDocInfo.originalUploadDate)}) loaded. Upload a new CSV if needed.
                              </p>
                         ) : (
                             <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md mx-auto">
                                 Upload your sales associate CSV to begin.
                             </p>
                         )}
                         <label htmlFor="csvUploadLarge" className="cursor-pointer bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-600 hover:to-sky-600 text-white font-bold py-3 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-150 ease-in-out inline-flex items-center space-x-2 text-lg">
                             <UploadCloud size={24} />
                             <span>Upload Sales CSV</span>
                         </label>
                         <input type="file" id="csvUploadLarge" accept=".csv" onChange={handleFileUpload} className="hidden" />
                     </div>
                )}

                {(processedAssociates && processedAssociates.length > 0) && ( /* Main Data Display JSX */
                    <>
                        {isPremiumUser ? ( /* Premium Focus Zone JSX */
                            <section className="mb-8 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-xl">
                                <div className="flex items-center space-x-3 mb-6 pb-3 border-b-2 border-yellow-500 dark:border-yellow-400">
                                    <Award className="h-8 w-8 text-yellow-500 dark:text-yellow-400" />
                                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Focus Zone (Premium)</h2>
                                </div>
                                <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6">
                                    <div className="p-1 bg-gradient-to-br from-green-400 to-emerald-500 rounded-lg shadow-lg"><div className="bg-white dark:bg-gray-800 p-4 rounded-md"><div className="flex items-center space-x-2 mb-3 text-green-600 dark:text-green-300"><CheckCircle size={24} /><h3 className="text-xl font-semibold">Already PC Qualified ({focusZoneLists.qualified.length})</h3></div><div className="max-h-96 overflow-y-auto pr-2 space-y-3 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600">{focusZoneLists.qualified.length > 0 ? focusZoneLists.qualified.map(assoc => <AssociateCard key={assoc.id} associate={assoc} type="qualified" />) : <p className="text-gray-500 dark:text-gray-400">No associates qualified.</p>}</div></div></div>
                                    <div className="p-1 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-lg shadow-lg"><div className="bg-white dark:bg-gray-800 p-4 rounded-md"><div className="flex items-center space-x-2 mb-3 text-yellow-600 dark:text-yellow-300"><TrendingUp size={24} /><h3 className="text-xl font-semibold">Close to Qualifying ({focusZoneLists.closeToQualifying.length})</h3></div><div className="max-h-96 overflow-y-auto pr-2 space-y-3 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600">{focusZoneLists.closeToQualifying.length > 0 ? focusZoneLists.closeToQualifying.map(assoc => <AssociateCard key={assoc.id} associate={assoc} type="close" />) : <p className="text-gray-500 dark:text-gray-400">No associates here.</p>}</div></div></div>
                                    <div className="p-1 bg-gradient-to-br from-red-400 to-rose-500 rounded-lg shadow-lg"><div className="bg-white dark:bg-gray-800 p-4 rounded-md"><div className="flex items-center space-x-2 mb-3 text-red-600 dark:text-red-300"><AlertTriangle size={24} /><h3 className="text-xl font-semibold">Danger of Losing Streak ({focusZoneLists.dangerZone.length})</h3></div><div className="max-h-96 overflow-y-auto pr-2 space-y-3 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600">{focusZoneLists.dangerZone.length > 0 ? focusZoneLists.dangerZone.map(assoc => <AssociateCard key={assoc.id} associate={assoc} type="danger" />) : <p className="text-gray-500 dark:text-gray-400">No one in danger zone.</p>}</div></div></div>
                                </div>
                            </section>
                        ) : ( /* Upgrade to Premium Section JSX */
                            <section className="mb-8 p-6 bg-blue-50 dark:bg-blue-900/80 border-l-4 border-blue-500 dark:border-blue-400 rounded-r-lg shadow-lg text-center">
                                <div className="flex flex-col items-center space-y-3 mb-3">
                                    <Mail className="h-10 w-10 text-blue-600 dark:text-blue-300" />
                                    <h2 className="text-2xl font-bold text-blue-800 dark:text-blue-200">Upgrade to Premium!</h2>
                                </div>
                                <p className="text-blue-700 dark:text-blue-300 mb-2">Unlock the "Focus Zone" and other powerful insights to boost your team's performance.</p>
                                <p className="text-blue-700 dark:text-blue-300 mb-4">Contact us for more information on upgrading to the Premium tier.</p>
                                <button
                                    onClick={handleUpgradeToPremium}
                                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-150 ease-in-out">
                                    <Star size={20} />
                                    <span>Request Premium Info</span>
                                </button>
                            </section>
                        )}

                        <section className="p-4 md:p-6 bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-x-auto"> {/* All Associates Data Table JSX */}
                            <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100 pb-3 border-b border-gray-300 dark:border-gray-700">All Associates Data</h2>
                            {(sortedAssociates && sortedAssociates.length > 0) ? (
                                <table className="w-full min-w-[1000px] text-sm">
                                    <thead className="bg-gray-100 dark:bg-gray-700">
                                        <tr>
                                            <SortableTh sortKey="name">Associate Name</SortableTh>
                                            <SortableTh sortKey="totalPremium">Current Points</SortableTh>
                                            <SortableTh sortKey="pointsNeeded">Points Needed</SortableTh>
                                            <SortableTh sortKey="carryoverPoints">Carryover</SortableTh>
                                            <SortableTh sortKey="isQualified">PC Qualified?</SortableTh>
                                            <SortableTh sortKey="consecutiveCount">Consecutive Months</SortableTh>
                                            <SortableTh sortKey="bonus.amount">Bonus Amt.</SortableTh>
                                            <th className="p-3 text-left">Next Bonus In</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {sortedAssociates.map(assoc => (
                                            <tr key={assoc.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${assoc.isQualified ? 'bg-green-50/30 dark:bg-green-900/20' : assoc.closeColor ? assoc.closeColor.replace('bg-', 'bg-opacity-30 dark:bg-opacity-20 ') : ''}`}>
                                                <td className="p-3 font-medium whitespace-nowrap">{assoc.name}</td>
                                                <td className="p-3 whitespace-nowrap text-center">{assoc.totalPremium.toFixed(0)}</td>
                                                <td className="p-3 whitespace-nowrap text-center">{assoc.pointsNeeded > 0 ? assoc.pointsNeeded.toFixed(0) : '-'}</td>
                                                <td className="p-3 whitespace-nowrap text-center">{assoc.carryoverPoints > 0 ? assoc.carryoverPoints.toFixed(0) : '-'}</td>
                                                <td className="p-3 whitespace-nowrap flex justify-center">{assoc.isQualified ? <CheckCircle className="text-green-500" /> : <XCircle className="text-red-500" />}</td>
                                                <td className="p-3 whitespace-nowrap text-center">{assoc.consecutiveCount}</td>
                                                <td className="p-3 whitespace-nowrap text-center">${assoc.bonus.amount} ({assoc.bonus.category})</td>
                                                <td className="p-3 whitespace-nowrap">{assoc.bonus.nextBonusIn} {assoc.bonus.nextBonusName}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="text-gray-500 dark:text-gray-400 p-4 text-center">
                                    {csvDataText ? "No associate data in current file." : "No data. Upload a CSV."}
                                </p>
                            )}
                        </section>
                    </>
                )}
                <footer className="mt-12 text-center text-sm text-gray-500 dark:text-gray-400 py-6">
                    <p>&copy; {new Date().getFullYear()} Performance Club Tracker. Data App ID: {firestoreDataAppId || "Not Set"}.</p>
                </footer>
            </div>
        </div>
    );
};

export default App;
